import { formatDateTime } from "../../utils/applicationFormatters";
import styles from "../../styles/applicationDetail.module.css";
import actionStyles from "../../styles/actionButtons.module.css";
import {
  isPaymentNameOverrideSectionVisible,
  NAME_OVERRIDE_REASON_OPTIONS,
} from "./signaturePaymentTabLogic";
import type { SignaturePaymentSignatureSectionProps } from "../../types";

export function SignaturePaymentSignatureSection({
  tab,
}: SignaturePaymentSignatureSectionProps) {
  const {
    applicationData,
    parsedSignature,
    canClearSignature,
    clearingSignature,
    nameOverrideChoice,
    nameOverrideReasonSelect,
    isSubmittingOverride,
    setShowAgreementsModal,
    setClearSignatureConfirmOpen,
    setNameOverrideChoice,
    setNameOverrideReasonSelect,
    handleDownloadChargebackPacket,
    handleSaveNameOverride,
  } = tab;

  if (!applicationData) return null;

  return (
    <div
      className={styles.section}
      data-testid="application-signature-payment-tab-section-government-id-signature"
    >
      <div className={styles.signatureNameOverrideRow}>
        <div className={styles.signatureNameOverrideMain}>
          {parsedSignature.signature && (
            <div className={styles.buttonRow} style={{ marginTop: "12px" }}>
              <button
                type="button"
                className={styles.btnSmall}
                data-testid="application-signature-payment-tab-agreements-section-button"
                onClick={() => setShowAgreementsModal(true)}
              >
                Agreements Section
              </button>
              {applicationData.is_chargeback && (
                <button
                  className={styles.btnSmall}
                  data-testid="application-signature-payment-tab-download-chargeback-packet-button"
                  onClick={() => void handleDownloadChargebackPacket()}
                >
                  Download Chargeback Packet
                </button>
              )}
            </div>
          )}

          <div className={styles.signatureMetaLines}>
            <p>
              Signature Status :{" "}
              {parsedSignature.signature
                ? `Signed on ${formatDateTime(applicationData.signature_date)}`
                : "Pending"}
            </p>
            <p>Signed: {parsedSignature.signature || ""}</p>
            <p>Device IP: {applicationData.ip_address || ""}</p>
          </div>

          {canClearSignature && (
            <div style={{ marginTop: "18px" }}>
              <button
                type="button"
                className={`${actionStyles.btn} ${actionStyles.btnPrimary}`}
                data-testid="application-signature-payment-tab-clear-name-button"
                onClick={() => setClearSignatureConfirmOpen(true)}
                disabled={clearingSignature}
              >
                {clearingSignature ? "Clearing..." : "Clear Name"}
              </button>
            </div>
          )}

          {parsedSignature.isImageDataUrl && (
            <div style={{ marginTop: "12px" }}>
              <img
                src={parsedSignature.signature}
                alt="Signature"
                className={styles.signatureImage}
              />
            </div>
          )}
        </div>

        {isPaymentNameOverrideSectionVisible(applicationData) && (
          <div className={styles.signatureNameOverrideColumnWrap}>
            <div className={styles.signatureNameOverrideAside}>
              <div className={styles.nameOverridePanel}>
                <div className={styles.nameOverrideRow}>
                  <span className={styles.nameOverrideLabel}>Name Override:</span>
                  <div
                    className={styles.nameOverrideRadios}
                    role="group"
                    aria-label="Name override"
                  >
                    <label
                      className={styles.nameOverrideRadioLabel}
                      data-testid="application-signature-payment-tab-name-override-yes-label"
                    >
                      <input
                        type="radio"
                        name="payment_name_override"
                        value="YES"
                        checked={nameOverrideChoice === "YES"}
                        data-testid="application-signature-payment-tab-name-override-yes-input"
                        onChange={() => setNameOverrideChoice("YES")}
                      />
                      Yes
                    </label>
                    <label
                      className={styles.nameOverrideRadioLabel}
                      data-testid="application-signature-payment-tab-name-override-no-label"
                    >
                      <input
                        type="radio"
                        name="payment_name_override"
                        value="NO"
                        checked={nameOverrideChoice === "NO"}
                        data-testid="application-signature-payment-tab-name-override-no-input"
                        onChange={() => setNameOverrideChoice("NO")}
                      />
                      No
                    </label>
                  </div>
                </div>
                <div className={styles.nameOverrideRow}>
                  <span className={styles.nameOverrideLabel}>Reason:</span>
                  <select
                    id="payment_name_override_reason"
                    className={styles.nameOverrideSelect}
                    value={nameOverrideReasonSelect}
                    data-testid="application-signature-payment-tab-name-override-reason-select"
                    onChange={(e) => setNameOverrideReasonSelect(e.target.value)}
                  >
                    <option value="">Select Reason</option>
                    {NAME_OVERRIDE_REASON_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={styles.nameOverrideRow}>
                  <button
                    type="button"
                    className={styles.btnSmall}
                    disabled={
                      isSubmittingOverride ||
                      (nameOverrideChoice === "YES" && !nameOverrideReasonSelect.trim())
                    }
                    data-testid="application-signature-payment-tab-save-name-override-button"
                    onClick={() => void handleSaveNameOverride()}
                  >
                    {isSubmittingOverride ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
